import React from 'react'
import { Button, Spinner, Badge, Alert, Form } from 'react-bootstrap'
import styles from './Repo.module.css'
import { connect } from 'react-redux'
import useGitHubRepos from '../../hooks/useGitHubRepos.js'
import SuccessBox from 'components/CreateUpdateSiteModal/components/SuccessBox'
import FileListModal from 'components/FileListModal/FileListModal'
import CustomModal from 'components/CustomModal/CustomModal'
import DomainSelector from './components/DomainSelector'
import ConfirmationModal from 'components/ConfirmationModal'
import { formatFileSize } from 'utils/general'

const RepoModal = ({ show, onHide, userProfile, liveSitesCount, githubData, onUpdate, customDomains, activeSpace }) => {
  const {
    allRepos,
    deploying,
    pageState,
    newSite,
    files,
    onClose,
    error,
    dismissError,
    selectedRepo,
    toggleRepoDetails,
    onOpenUrl,
    formatDate,
    formatDateTime,
    showFileList,
    setShowFileList,
    htmlFilesForRepo,
    phpFilesForRepo,
    showingPhpFiles,
    currentRepoForFiles,
    handleFileSelected,
    isDeployButtonDisabled,
    showFileSelector,
    handleDeployClick,
    renderFilesBadge,
    isRepoSizeExceeded,
    onReset,
    handleDomainChange,
    handleResyncRepos,
    handleRedeploy,
    resyncing,
    errorRepo,
    needsReconnect,
    showDisconnectConfirm,
    setShowDisconnectConfirm,
    disconnecting,
    handleDisconnect,
    handleToggleAutoDeploy,
    tlds
  } = useGitHubRepos(userProfile, githubData, liveSitesCount, onHide, onUpdate, activeSpace)

  return (
    <CustomModal show={show} onHide={onHide} aria-labelledby="contained-modal-title-vcenter" centered size="lg">
      {pageState === 'SUCCESS' ? (
        <SuccessBox
          userProfile={userProfile}
          site={newSite}
          files={files}
          onClose={onClose}
          onReset={() => onReset(newSite)}
        />
      ) : (
        <div className={styles.container}>
          {pageState === 'LOADING' && (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingContent}>
                <div className={styles.loadingAnimation}>
                  <img src="/assets/icons/github-icon.svg" alt="GitHub" className={styles.pulsingIcon} />
                  <Spinner animation="border" variant="primary" className={styles.spinner} />
                </div>
                <h4 className={styles.loadingTitle}>Fetching your repositories</h4>
                <p className={styles.loadingMessage}>Hold tight while we connect to GitHub...</p>
              </div>
            </div>
          )}

          {pageState === 'CONNECTED' && needsReconnect && (
            <Alert variant="warning" className={styles.statusAlert}>
              <div className={styles.alertContent}>
                <strong>Your GitHub connection has expired.</strong> Reconnect to keep syncing and auto-deploying your
                repositories. Your sites are still live.
              </div>
              <Button className={styles.addButton} onClick={onOpenUrl}>
                Reconnect GitHub
              </Button>
            </Alert>
          )}

          {pageState === 'CONNECTED' && error && !errorRepo && (
            <Alert variant="danger" className={styles.statusAlert} dismissible onClose={dismissError}>
              {error}
            </Alert>
          )}

          {pageState === 'CONNECTED' && allRepos.length > 0 && (
            <>
              <div className={styles.header}>
                <div className={styles.headerContent}>
                  <span className={styles.headerTitle}>Import Git Repository</span>
                  <p className={styles.headerDescription}>
                    Select a repository to deploy. Use the &quot;Add project&quot; button to configure repository
                    permissions.
                  </p>
                </div>

                <div className={styles.headerButtons}>
                  {allRepos.length > 0 && (
                    <Button className={styles.resyncButton} onClick={handleResyncRepos} disabled={resyncing}>
                      {resyncing ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <img src="/assets/icons/sync.svg" alt="resync-icon" /> Resync
                        </>
                      )}
                    </Button>
                  )}
                  <Button className={styles.addButton} onClick={onOpenUrl}>
                    <img src="/assets/icons/plus-circle.svg" alt="add-icon" /> Add project
                  </Button>
                  <Button className={styles.disconnectButton} onClick={() => setShowDisconnectConfirm(true)}>
                    Disconnect
                  </Button>
                </div>
              </div>

              <Alert variant="info" className={styles.deploymentAlert}>
                <div className={styles.alertIcon}>
                  <img src="/assets/icons/info-circle.svg" alt="info" width="20" height="20" />
                </div>
                <div className={styles.alertContent}>
                  <strong>Important:</strong> Ensure your repository includes a build folder (dist or build) for
                  successful deployment.
                </div>
              </Alert>

              <div className={styles.repos}>
                {allRepos.map((repo) => {
                  const isSelected = selectedRepo && selectedRepo.id === repo.id
                  const filesBadgeInfo = renderFilesBadge(repo)

                  return (
                    <div key={repo.id} className={`${styles.repo} ${isSelected ? styles.expanded : ''}`}>
                      <div className={styles.repoContent} onClick={() => toggleRepoDetails(repo)}>
                        <div className={styles.repoDetails}>
                          <img src="/assets/icons/github-icon.svg" alt="GitHub" width="24" height="24" />
                          <div className={styles.repoInfo}>
                            <div className={styles.repoHeader}>
                              <span className={styles.repoName}>{repo.name}</span>
                              {repo.private && (
                                <Badge className={styles.privateBadge} bg="grey">
                                  Private
                                </Badge>
                              )}
                              {repo.accessRemoved && (
                                <Badge className={styles.privateBadge} bg="warning" text="dark">
                                  No access
                                </Badge>
                              )}
                              {!repo.accessRemoved && repo.lastDeployStatus === 'failed' && (
                                <Badge className={styles.privateBadge} bg="danger">
                                  Last deploy failed
                                </Badge>
                              )}
                            </div>
                            <div className={styles.repoMeta}>
                              <span className={styles.lastUpdated}>Updated {formatDate(repo.updated_at)}</span>
                              {repo.language && (
                                <span className={styles.language}>
                                  <span
                                    className={styles.languageDot}
                                    style={{
                                      backgroundColor:
                                        repo.language === 'JavaScript'
                                          ? '#f1e05a'
                                          : repo.language === 'TypeScript'
                                            ? '#2b7489'
                                            : repo.language === 'HTML'
                                              ? '#e34c26'
                                              : repo.language === 'CSS'
                                                ? '#563d7c'
                                                : '#dddddd'
                                    }}
                                  />
                                  {repo.language}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className={styles.actionWrapper}>
                          <Button
                            className={styles.expandButton}
                            variant="link"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleRepoDetails(repo)
                            }}
                          >
                            <img
                              src={`/assets/icons/${isSelected ? 'chevron-up.svg' : 'chevron-down.svg'}`}
                              alt={isSelected ? 'Less' : 'More'}
                              width="20"
                              height="20"
                            />
                          </Button>
                        </div>
                      </div>

                      {isSelected && (
                        <div className={styles.expandedDetails}>
                          {error && errorRepo === repo.full_name && (
                            <div className={styles.errorAlert}>
                              <div className={styles.errorAlertContent}>
                                <div className={styles.errorIcon}>
                                  <img src="/assets/icons/warning-yellow.svg" alt="Error" width="24" height="24" />
                                </div>
                                <div className={styles.errorMessage}>{error}</div>
                                <button
                                  className={styles.closeButton}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    dismissError()
                                  }}
                                  aria-label="Close"
                                >
                                  <img src="/assets/icons/clear-icon.svg" alt="close" width="24" height="24" />
                                </button>
                              </div>
                            </div>
                          )}
                          <div className={styles.detailsGrid}>
                            <div className={styles.detailItem}>
                              <div className={styles.detailLabel}>Created</div>
                              <div className={styles.detailValue}>{formatDate(repo.created_at)}</div>
                            </div>
                            <div className={styles.detailItem}>
                              <div className={styles.detailLabel}>Default Branch</div>
                              <div className={styles.detailValue}>{repo.default_branch}</div>
                            </div>
                            <div className={styles.detailItem}>
                              <div className={styles.detailLabel}>Last Commit</div>
                              <div className={styles.detailValue}>{formatDate(repo.updated_at)}</div>
                            </div>
                            <div className={styles.detailItem}>
                              <div className={styles.detailLabel}>Size</div>
                              <div className={styles.detailValue}>
                                <span className={isRepoSizeExceeded(repo.size) ? styles.sizeExceeded : ''}>
                                  {formatFileSize(repo.size * 1024)}
                                </span>
                              </div>
                            </div>
                            {repo.deployed && (
                              <div className={styles.detailItem}>
                                <div className={styles.detailLabel}>Status</div>
                                <div className={styles.detailValue}>
                                  <div className={styles.deploymentStatus}>
                                    <span className={styles.statusDot}></span>
                                    <span className={styles.deployedText}>Deployed</span>
                                    {repo.lastDeployed && (
                                      <span className={styles.deploymentDate}>{formatDateTime(repo.lastDeployed)}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          {repo.accessRemoved && (
                            <div className={styles.deploymentNote}>
                              <img src="/assets/icons/info-circle.svg" alt="info" width="16" height="16" />
                              <span>
                                Tiiny Host no longer has access to this repository. Your site is still live — use
                                &quot;Add project&quot; to grant access again and resume syncing.
                              </span>
                            </div>
                          )}
                          {!repo.accessRemoved && repo.lastDeployStatus === 'failed' && (
                            <div className={styles.deploymentNote}>
                              <img src="/assets/icons/warning-yellow.svg" alt="warning" width="16" height="16" />
                              <span>
                                Last deploy failed
                                {repo.lastFailedAt ? ` on ${formatDateTime(repo.lastFailedAt)}` : ''}. Use Sync now to
                                try again.
                              </span>
                            </div>
                          )}
                          {repo.deployed && repo.domain && !repo.accessRemoved && (
                            <div className={styles.detailItem}>
                              <Form.Check
                                type="switch"
                                id={`auto-deploy-${repo.id}`}
                                label="Auto-deploy on push to default branch"
                                checked={repo.autoDeploy !== false}
                                onChange={() => handleToggleAutoDeploy(repo)}
                              />
                            </div>
                          )}
                          {!repo.deployed && filesBadgeInfo && (
                            <div className={styles.detailItem}>
                              <div className={styles.detailLabel}>{filesBadgeInfo.fileType} Files</div>
                              <div className={styles.detailValue}>
                                Multiple {filesBadgeInfo.fileType} files found{' '}
                                {filesBadgeInfo.selectedFile && <span> (Index: {filesBadgeInfo.selectedFile})</span>}
                                <Button
                                  variant="link"
                                  size="sm"
                                  className={styles.selectFileButton}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    showFileSelector(selectedRepo, filesBadgeInfo.fileType === 'PHP')
                                  }}
                                >
                                  {filesBadgeInfo.selectedFile ? 'Change index' : 'Select index'}
                                </Button>
                              </div>
                            </div>
                          )}
                          {repo.deployed && repo.domain ? (
                            <div className={styles.detailItem}>
                              <div className={styles.detailLabel}>Live URL</div>
                              <div className={styles.detailValue}>
                                <a
                                  href={`https://${repo.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={styles.domainLink}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {repo.domain}
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className={styles.deploymentNote}>
                              <img src="/assets/icons/info-circle.svg" alt="info" width="16" height="16" />
                              <span>
                                Deployment will automatically search for build files (<code>dist/</code> or
                                <code>build/</code>) in your directories.
                              </span>
                            </div>
                          )}
                          {!repo.deployed && (
                            <div className={styles.domainSelectorSection}>
                              <DomainSelector
                                userProfile={userProfile}
                                customDomains={customDomains || []}
                                tlds={tlds}
                                placeholder="my-awesome-site"
                                disabled={deploying === repo.full_name}
                                onDomainChange={(domainData) => handleDomainChange(repo.id, domainData)}
                                onHide={onHide}
                              />
                            </div>
                          )}

                          <div className={styles.expandedActions}>
                            <div className={styles.actionButtonsGroup}>
                              {deploying === repo.full_name ? (
                                <div className={styles.deployingWrapper}>
                                  <Spinner animation="border" variant="primary" size="sm" />
                                  <span className={styles.deployingText}>Deploying...</span>
                                </div>
                              ) : repo.deployed ? (
                                <div className={styles.deployedStatus}>
                                  <Button
                                    className={styles.viewSiteButton}
                                    variant="outline-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      if (repo.domain) {
                                        window.open(`https://${repo.domain}`, '_blank')
                                      }
                                    }}
                                    disabled={!repo.domain}
                                  >
                                    View live site
                                  </Button>
                                  <Button
                                    className={styles.redeployButton}
                                    variant="primary"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleRedeploy(repo)
                                    }}
                                    disabled={deploying === repo.full_name || repo.accessRemoved}
                                  >
                                    Sync now
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <Button
                                    className={styles.viewOnGithubButton}
                                    variant="outline-secondary"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      window.open(repo.html_url, '_blank')
                                    }}
                                  >
                                    <img src="/assets/icons/link.svg" alt="external link" width="18" height="18" />
                                    View on GitHub
                                  </Button>
                                  {filesBadgeInfo && filesBadgeInfo.count > 1 && !filesBadgeInfo.selectedFile ? (
                                    <Button
                                      className={styles.selectFileButtonInline}
                                      variant="outline-primary"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        showFileSelector(repo, filesBadgeInfo.fileType === 'PHP')
                                      }}
                                    >
                                      Select index file
                                    </Button>
                                  ) : (
                                    <Button
                                      className={styles.deployButton}
                                      variant="primary"
                                      disabled={isDeployButtonDisabled(repo)}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeployClick(repo)
                                      }}
                                    >
                                      Deploy
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {pageState === 'CONNECTED' && allRepos.length === 0 && (
            <div className={styles.noRepo}>
              <div className={styles.emptyStateAnimation}>
                <img
                  src="/assets/icons/github-icon.svg"
                  alt="GitHub"
                  width="64"
                  height="64"
                  className={styles.emptyStateIcon}
                />
              </div>
              <div className={styles.noRepoText}>No repositories found</div>
              <p className={styles.noRepoDescription}>
                Use the &quot;Add project&quot; button to configure repository access permissions. Ensure your
                repositories include a build folder for successful deployment.
              </p>
              <Button className={styles.addButton} onClick={onOpenUrl}>
                <img src="/assets/icons/plus-circle.svg" alt="add-icon" />
                Add Project
              </Button>
              <Button variant="link" className={styles.disconnectLink} onClick={() => setShowDisconnectConfirm(true)}>
                Disconnect GitHub
              </Button>
            </div>
          )}

          <ConfirmationModal
            show={showDisconnectConfirm}
            message="Disconnect GitHub? Your sites stay live, but they won't update when you push until you reconnect. Reconnecting later restores the links to your repositories."
            handleNegative={() => setShowDisconnectConfirm(false)}
            handlePositive={handleDisconnect}
            actionProcessing={disconnecting}
          />

          {showFileList && (
            <FileListModal
              show={showFileList}
              onHide={() => setShowFileList(false)}
              onFileSelected={handleFileSelected}
              filesList={showingPhpFiles ? phpFilesForRepo : htmlFilesForRepo}
              indexFile={currentRepoForFiles?.selectedIndexFile || ''}
              title={`Which ${showingPhpFiles ? 'PHP' : 'HTML'} file should be your homepage?`}
              subtitle={`We've found multiple ${showingPhpFiles ? 'PHP' : 'HTML'} files in your repository`}
            />
          )}
        </div>
      )}
    </CustomModal>
  )
}

const mapStateToProps = ({ manage }) => ({
  selectedFile: manage?.draggedFile,
  userProfile: manage.userProfile,
  githubData: manage.githubData,
  customDomains: manage.customDomains,
  activeSpace: manage.activeSpace
})

export default connect(mapStateToProps)(RepoModal)
